import { createSelector } from 'reselect';
import {
  gtiSelector,
  notificationSelector,
  globalNotificationSelector,
  questionnaireSelector,
  timelineSelector,
} from '../selector';
import { isEmpty, concat } from 'lodash';
import { PlaceHolderIds } from '../../components/PagePlaceholder';
import { shouldShowPlaceholder } from '../../utils/Functions';
import {
  notificationIdentity,
  QUESTIONNAIRE_PAGE_NOTIFICATION,
} from '../../definitions/NotificationConstants';

const selectQuestionnaireAppointments = createSelector(
  [questionnaireSelector, timelineSelector],
  (questionnaireSelector, timelineSelector) => {
    if (questionnaireSelector.isFetching || timelineSelector.isFetching) {
      return [];
    }

    let appointmentData = timelineSelector.data;
    let { active, started, completed } = questionnaireSelector.data.medixine;
    let { questionnaires } = questionnaireSelector.data.omavointi;
    let questionnaireData = concat(
      [],
      active,
      started,
      completed,
      questionnaires
    );

    if (
      !appointmentData ||
      !questionnaireData ||
      (!active && !started && !completed)
    )
      return [];

    let questionnaireIds = questionnaireData
      .filter((x) => x.fhirAppointmentId)
      .map((x) => x.fhirAppointmentId);

    let formAssignmentQuestionnaireIds = questionnaireSelector.assignedForms
      .filter((x) => x.timelineEventId)
      .map((x) => x.timelineEventId);

    questionnaireIds.push(...formAssignmentQuestionnaireIds);
    return appointmentData
      .filter((x) => questionnaireIds.indexOf(x.id) >= 0)
      .map((x) => {
        return {
          id: x.id,
          department: x.department,
          date: x.date,
        };
      });
  }
);

const selectShowActiveQuestionnaireDescription = createSelector(
  [questionnaireSelector, timelineSelector],
  (questionnaireSelector, timelineSelector) => {
    if (questionnaireSelector.error || timelineSelector.error) {
      // one or other errors, we cannot have further info so rather
      // show unspecified information
      return true;
    } else {
      // if neither has error, then wait until both are fetched
      return !(questionnaireSelector.isFetching || timelineSelector.isFetching);
    }
  }
);

const selectQuestionnaireNotifications = createSelector(
  [notificationSelector],
  (notificationSelector) => {
    return notificationSelector.data.filter(
      (notification) =>
        notification[notificationIdentity].indexOf(
          QUESTIONNAIRE_PAGE_NOTIFICATION
        ) >= 0
    );
  }
);

const _selectShowCoronaQuestionnaire = (globalNotificationSelector) => {
  let result = globalNotificationSelector.isFetching
    ? false
    : globalNotificationSelector.data &&
      globalNotificationSelector.data.filter(
        (notification) => notification.notificationKey === 'coronaInfo'
      ).length === 1;
  return result;
};

const selectShowCoronaQuestionnaire = createSelector(
  [globalNotificationSelector],
  (globalNotificationSelector) => {
    return _selectShowCoronaQuestionnaire(globalNotificationSelector);
  }
);

const selectShowQuestionnairePagePlaceholder = createSelector(
  [questionnaireSelector, gtiSelector, globalNotificationSelector],
  (questionnaireSelector, gtiSelector, globalNotificationSelector) => {
    let show = false;
    let { data, error, assignedForms } = questionnaireSelector;
    let { medixine, omavointi } = data;
    let isFetching =
      questionnaireSelector.isFetching || globalNotificationSelector.isFetching;
    let coronaQuestionnaire = _selectShowCoronaQuestionnaire(
      globalNotificationSelector
    );

    if (isFetching) {
      return PlaceHolderIds.LOADER;
    }

    if (
      !isFetching &&
      error == null &&
      isEmpty(medixine.active) &&
      isEmpty(medixine.started) &&
      isEmpty(medixine.completed) &&
      isEmpty(omavointi.questionnaires) &&
      medixine.successful !== false &&
      omavointi.successful !== false &&
      gtiSelector.gtiStatus == null &&
      !coronaQuestionnaire &&
      isEmpty(assignedForms)
    ) {
      show = true;
    }
    return shouldShowPlaceholder(show, PlaceHolderIds.QUESTIONNAIRES);
  }
);

export {
  selectShowActiveQuestionnaireDescription,
  selectQuestionnaireAppointments,
  selectQuestionnaireNotifications,
  selectShowQuestionnairePagePlaceholder,
  selectShowCoronaQuestionnaire,
};
